import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, Phone, Mail, User, CheckCircle, XCircle, FileText, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useLeadStore } from '@/store/leadStore';
import { useActivityStore } from '@/store/activityStore';
import { useReminderStore } from '@/store/reminderStore';
import { useNoteStore } from '@/store/noteStore';
import { formatDate, formatCurrency } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';
import { ActivityTimeline } from '@/components/activities/ActivityTimeline';
import { ActivityFormDialog } from '@/components/activities/ActivityFormDialog';
import { ReminderList } from '@/components/reminders/ReminderList';
import { AttachmentSection } from '@/components/attachments/AttachmentSection';
import { LeadFormDialog } from '@/components/leads/LeadFormDialog';
import { NoteList } from '@/components/notes/NoteList';
import type { LeadStatus, LeadPriority, Reminder, Note } from '@/types/crm';

const statusColors: Record<LeadStatus, string> = {
  new: 'bg-blue-100 text-blue-800',
  qualified: 'bg-green-100 text-green-800',
  disqualified: 'bg-gray-100 text-gray-800',
  converted: 'bg-purple-100 text-purple-800',
};

const priorityColors: Record<LeadPriority, string> = {
  low: 'bg-gray-100 text-gray-800',
  medium: 'bg-blue-100 text-blue-800',
  high: 'bg-orange-100 text-orange-800',
  urgent: 'bg-red-100 text-red-800',
};

export function LeadDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { selectedLead, fetchLeadById, updateLead, convertLead } = useLeadStore();
  const { fetchActivitiesByEntity } = useActivityStore();
  const { reminders, fetchRemindersByLead } = useReminderStore();
  const { notes, fetchNotesByLead } = useNoteStore();
  const { toast } = useToast();
  const [activities, setActivities] = useState<any[]>([]);
  const [localReminders, setLocalReminders] = useState<Reminder[]>([]);
  const [localNotes, setLocalNotes] = useState<Note[]>([]);
  const [showActivityDialog, setShowActivityDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      if (id) {
        setIsLoading(true);
        await fetchLeadById(id);
        const acts = await fetchActivitiesByEntity('lead', id);
        setActivities(acts);
        const rems = await fetchRemindersByLead(id);
        setLocalReminders(rems);
        const nts = await fetchNotesByLead(id);
        setLocalNotes(nts);
        setIsLoading(false);
      }
    };
    loadData();
  }, [id, fetchLeadById, fetchActivitiesByEntity, fetchRemindersByLead, fetchNotesByLead]);

  useEffect(() => {
    setLocalReminders(reminders);
  }, [reminders]);

  useEffect(() => {
    setLocalNotes(notes);
  }, [notes]);

  const handleStatusChange = async (status: LeadStatus) => {
    if (id) {
      try {
        await updateLead(id, { status });
        await fetchLeadById(id);
        toast({
          title: 'Status updated',
          description: `Lead status changed to ${status}`,
        });
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to update status',
          variant: 'destructive',
        });
      }
    }
  };

  const handleConvert = async () => {
    if (id) {
      try {
        const result = await convertLead(id);
        if (result) {
          toast({
            title: 'Lead converted',
            description: 'Lead has been converted successfully.',
          });
          navigate(`/deals/${result.deal_id}`);
        }
      } catch (error: any) {
        const is409 = error?.code === '409' || error?.status === 409 || error?.message?.toLowerCase().includes('conflict') || error?.message?.toLowerCase().includes('already converted');
        toast({
          title: is409 ? 'Lead already converted' : 'Error',
          description: is409
            ? 'This lead has already been converted to a company, contact, and deal.'
            : error?.message || 'Failed to convert lead',
          variant: 'destructive',
        });
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!selectedLead) {
    return (
      <div className="p-6">
        <p>Lead not found</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/leads')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{selectedLead.company_name}</h1>
              <Badge className={statusColors[selectedLead.status]} variant="secondary">
                {selectedLead.status}
              </Badge>
              <Badge className={priorityColors[selectedLead.priority]} variant="secondary">
                {selectedLead.priority}
              </Badge>
            </div>
            <p className="text-muted-foreground">{selectedLead.lead_number}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowEditDialog(true)}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit Lead
          </Button>
          {selectedLead.status === 'new' && (
            <>
              <Button onClick={() => handleStatusChange('qualified')} className="bg-green-600 hover:bg-green-700">
                <CheckCircle className="h-4 w-4 mr-2" />
                Mark as Qualified
              </Button>
              <Button variant="outline" onClick={() => handleStatusChange('disqualified')}>
                <XCircle className="h-4 w-4 mr-2" />
                Disqualify
              </Button>
            </>
          )}
          {selectedLead.status === 'qualified' && !selectedLead.converted_to_deal_id && (
            <Button onClick={handleConvert}>
              <ArrowUpRight className="h-4 w-4 mr-2" />
              Convert to Deal
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="reminders">
            <TabsList>
              <TabsTrigger value="reminders">Reminders</TabsTrigger>
              <TabsTrigger value="activities">Activities</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
              <TabsTrigger value="notes">Notes</TabsTrigger>
            </TabsList>
            <TabsContent value="reminders" className="mt-4">
              <ReminderList leadId={id!} reminders={localReminders} />
            </TabsContent>
            <TabsContent value="activities" className="mt-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">Activity Timeline</h3>
                <Button size="sm" onClick={() => setShowActivityDialog(true)}>
                  Add Activity
                </Button>
              </div>
              <ActivityTimeline activities={activities} />
            </TabsContent>
            <TabsContent value="documents" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Lead Documents
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <AttachmentSection entityType="lead" entityId={id!} />
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="notes" className="mt-4">
              <NoteList leadId={id!} notes={localNotes} />
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <User className="h-4 w-4 text-muted-foreground" />
                <span>{selectedLead.first_name} {selectedLead.last_name}</span>
              </div>
              {selectedLead.email && (
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <a href={`mailto:${selectedLead.email}`} className="text-primary hover:underline">
                    {selectedLead.email}
                  </a>
                </div>
              )}
              {selectedLead.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <a href={`tel:${selectedLead.phone}`} className="text-primary hover:underline">
                    {selectedLead.phone}
                  </a>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Lead Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Source</span>
                <span className="capitalize">{selectedLead.source?.replace('_', ' ') || '-'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Estimated Value</span>
                <span className="font-medium">{formatCurrency(selectedLead.estimated_value)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Assigned To</span>
                <span>{selectedLead.assigned_to_name || '-'}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Created</span>
                <span>{formatDate(selectedLead.created_at)}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Activity Dialog */}
      <ActivityFormDialog
        open={showActivityDialog}
        onOpenChange={setShowActivityDialog}
        entityType="lead"
        entityId={id}
      />

      {/* Edit Lead Dialog */}
      <LeadFormDialog
        open={showEditDialog}
        onOpenChange={(open) => {
          setShowEditDialog(open);
          if (!open) fetchLeadById(id!);
        }}
        lead={selectedLead}
      />
    </div>
  );
}
